import PortfolioArrowIcon from './PortfolioArrowIcon';
import PortfolioTreeWrapper from './PortfolioTreeWrapper';
import PortfolioNodeIcon from './PortfolioNodeIcon';
import { TreeNode } from 'components/react-tree/types';
import { updateNodeInTree } from 'components/react-tree/utils-ts';
import { TypedCustomCheckbox, TypedTree } from 'types/utils';
import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { Box, Flex, IconButton, Spacer } from '@chakra-ui/react';
import { PortfolioItemTreeNode } from 'types/portfolios';
import SearchBar from 'components/utils/SearchBar';
import { useIntl } from 'react-intl';
import { ReactComponent as CollapseIcon } from '../../icons/collapse.svg';
import { ReactComponent as ExpandAllIcon } from '../../icons/expand-all.svg';
import { isNodeIndeterminate } from 'components/wizard/wizards/pcw/util';

interface ItemsSelectProps {
    nodes: TreeNode<any>[];
    setNodes?: Dispatch<SetStateAction<TreeNode<any>[]>>;
    expanded: string[];
    setExpanded: Dispatch<SetStateAction<string[]>>;
    showSelect?: boolean;
    includeSearch?: boolean;
    expandedByDefault?: boolean;
}

/**
 * ItemsSelect is an alternative to ItemsSelectionComponent with the focus on rendering provided nodes.
 *
 * @param nodes - Tree structure with selection state embedded in each node
 * @param setNodes - Optional state setter for updating tree (required for selection functionality)
 * @param expanded - Array of expanded node IDs for controlling tree visibility
 * @param setExpanded - State setter for managing tree expansion
 * @param showSelect - Whether to display selection checkboxes (default: true)
 * @param includeSearch - Whether to include search functionality (default: true)
 * @param expandedByDefault - Whether nodes should be expanded by default (unused currently)
 */
export function ItemsSelect({
    nodes,
    setNodes,
    expanded,
    setExpanded,
    showSelect = true,
    includeSearch = true,
}: ItemsSelectProps) {
    const intl = useIntl();
    const [search, setSearch] = useState('');
    const [focused, setFocused] = useState<string | null>(null);

    const filteredNodes = useMemo(() => {
        const term = search.trimStart().toLowerCase();
        if (!term || !includeSearch) return nodes;

        if (term && term.length >= 2) {
            const data = nodes.reduce((acc: PortfolioItemTreeNode[], group: any) => {
                const filtered = group.nodes.filter((node: any) => node.properties?.name.toLowerCase().includes(term));

                if (filtered.length > 0) {
                    acc.push({
                        ...group,
                        nodes: filtered,
                    });
                }

                return acc;
            }, []);
            return data;
        }
        return nodes;
    }, [nodes, search, includeSearch]);

    const onSelectChange = useCallback(
        (node: TreeNode) => {
            if (!setNodes || node.isDisabled || node.isFolder) return;

            const isRootNode = node?.nodes && node.nodes.length > 0;
            if (isRootNode) {
                const isSelected = !node.isSelected;

                const updatedChildNodes = node.nodes.map((child) => ({
                    ...child,
                    isSelected: child.isDisabled ? child.isSelected : isSelected,
                }));

                setNodes((prev: TreeNode<any>[]) =>
                    updateNodeInTree(prev, node.id, () => ({
                        isSelected: isSelected,
                        nodes: updatedChildNodes,
                    }))
                );

                return;
            }

            setNodes((prev: TreeNode<any>[]) =>
                updateNodeInTree(prev, node.id, (node) => ({
                    isSelected: !node.isSelected,
                }))
            );
        },
        [setNodes]
    );

    const onFocusChange = useCallback(({ id }: TreeNode) => {
        setFocused(id);
    }, []);

    const onExpandChange = useCallback(
        ({ id }: TreeNode) => {
            setExpanded((prev) => (prev.includes(id) ? prev.filter((node) => node !== id) : prev.concat(id)));
        },
        [setExpanded]
    );

    const onClickToggleExpandCollapse = useCallback(
        (node: TreeNode) => {
            if (node?.isExpandable) {
                setExpanded((prev) =>
                    prev.includes(node.id) ? prev.filter((id) => id !== node.id) : prev.concat(node.id)
                );
                setFocused(node.id);
            }
        },
        [setExpanded]
    );

    const renderLabel = useCallback(
        (node: TreeNode) => {
            return (
                <div
                    className={`${node?.isExpandable ? '' : 'non-expandable '}portfolio-label-wrapper`}
                    onClick={() => onClickToggleExpandCollapse(node)}
                >
                    {node?.isExpandable && (
                        <span>
                            <PortfolioArrowIcon isExpanded={node?.isExpanded} isMaster={node.properties?.isMaster} />
                        </span>
                    )}

                    <div
                        className="portfolio-label"
                        onClick={(event) => {
                            if (event.shiftKey && node?.isExpandable) {
                                // parent div will take care of expand/collapse
                            } else {
                                onSelectChange(node);
                            }
                        }}
                    >
                        <PortfolioNodeIcon
                            isFolder={node.isFolder}
                            isExpanded={node?.isExpanded}
                            isMaster={node.properties?.isMaster}
                            typeId={node.properties?.typeId}
                        />

                        <span>{node.label}</span>
                    </div>

                    {showSelect && (
                        <TypedCustomCheckbox
                            tabIndex={-1}
                            onChange={() => onSelectChange(node)}
                            isDisabled={node?.isDisabled}
                            isChecked={node?.isSelected}
                            isIndeterminate={isNodeIndeterminate(node)}
                        />
                    )}
                </div>
            );
        },
        [onClickToggleExpandCollapse, onSelectChange, showSelect]
    );

    return (
        <Box>
            {includeSearch && (
                <>
                    <Flex alignItems="center">
                        <SearchBar
                            textHolder={intl.formatMessage({ id: 'portfolios_search_items' })}
                            onChange={(value: string) => setSearch(value)}
                            searchTerm={search}
                        />
                        <IconButton
                            ml={2}
                            aria-label={'collapse'}
                            size="xs"
                            icon={<CollapseIcon />}
                            variant="circular-icon"
                            onClick={() => setExpanded([])}
                        />
                        <IconButton
                            mx={2}
                            aria-label={'expand'}
                            size="xs"
                            icon={<ExpandAllIcon />}
                            variant="circular-icon"
                            onClick={() => setExpanded(filteredNodes.map((node) => node.id))}
                        />
                    </Flex>
                    <Spacer mb={5} />
                </>
            )}
            <Box overflow="auto" maxH={'500px'}>
                <PortfolioTreeWrapper isDisplayedInModal={false} isDisabled={false}>
                    <TypedTree
                        aria-label={'Items Selection'}
                        nodes={filteredNodes}
                        onSelectChange={onSelectChange}
                        expanded={expanded}
                        onExpandChange={onExpandChange}
                        focused={focused}
                        onFocusChange={onFocusChange}
                        renderLabel={renderLabel}
                        expandOnSelect={false}
                    />
                </PortfolioTreeWrapper>
            </Box>
        </Box>
    );
}
